/**
 * Shared types and constants for the CoreFlow Pages module.
 */

// ─── Enums / Union Types ────────────────────────────────────────────────────

/** Library visibility levels */
export type LibraryVisibility = 'private' | 'public-read' | 'public-edit';

/** Library member roles in descending privilege order */
export type LibraryMemberRole = 'admin' | 'editor' | 'viewer';

/** Page lifecycle status */
export type PageStatus = 'active' | 'archived' | 'deleted';

// ─── Interfaces ─────────────────────────────────────────────────────────────

/** Library entity metadata */
export interface Library {
  readonly libraryId: string;
  readonly name: string;
  readonly description: string;
  readonly visibility: LibraryVisibility;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

/** Library membership record */
export interface LibraryMember {
  readonly libraryId: string;
  readonly userId: string;
  readonly role: LibraryMemberRole;
  readonly addedBy: string;
  readonly addedAt: string;
}

/** Page entity metadata (stored in DynamoDB) */
export interface Page {
  readonly pageId: string;
  readonly title: string;
  readonly libraryId: string;
  readonly parentPageId: string | null;
  readonly authorId: string;
  readonly status: PageStatus;
  readonly contentHash: string;
  readonly currentRevisionTimestamp: string;
  readonly sortOrder: string;
  readonly searchText?: string;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

/** Revision index entry (stored in DynamoDB — content body is in S3) */
export interface PageRevision {
  readonly pageId: string;
  readonly timestamp: string;
  readonly authorId: string;
  readonly changeNote?: string;
  readonly contentHash: string;
}

/** Wiki link record (outgoing link from source page to target page) */
export interface WikiLink {
  readonly sourcePageId: string;
  readonly targetPageId: string;
  readonly displayText: string;
  readonly createdAt: string;
}

/** Cross-link record (page ↔ card or board) */
export interface CrossLink {
  readonly pageId: string;
  readonly entityType: 'card' | 'board';
  readonly entityId: string;
  readonly entityTitle: string;
  readonly createdAt: string;
}

/** Page template */
export interface PageTemplate {
  readonly templateId: string;
  readonly libraryId: string;
  readonly name: string;
  readonly content: unknown; // BlockNote JSON
  readonly createdBy: string;
  readonly isBuiltIn: boolean;
  readonly createdAt: string;
}

/** Page attachment metadata */
export interface PageAttachment {
  readonly attachmentId: string;
  readonly pageId: string;
  readonly filename: string;
  readonly mimeType: string;
  readonly size: number;
  readonly s3Key: string;
  readonly uploadedBy: string;
  readonly uploadedAt: string;
}


// ─── DynamoDB Key Pattern Constants ─────────────────────────────────────────

/** Primary key patterns */
export const PAGE_PK = (pageId: string): string => `PAGE#${pageId}`;
export const PAGE_META_SK = 'META';
export const PAGE_REVISION_SK = (timestamp: string): string => `REVISION#${timestamp}`;

export const LIBRARY_PK = (libraryId: string): string => `LIBRARY#${libraryId}`;
export const LIBRARY_META_SK = 'META';
export const LIBRARY_MEMBER_SK = (userId: string): string => `MEMBER#${userId}`;
/** SK prefix matching ALL member rows of a library (for begins_with queries). */
export const LIBRARY_MEMBER_SK_PREFIX = 'MEMBER#';

export const WIKILINK_SK = (targetPageId: string): string => `WIKILINK#${targetPageId}`;
export const CROSSLINK_SK = (entityType: string, entityId: string): string =>
  `CROSSLINK#${entityType}#${entityId}`;
export const ATTACHMENT_SK = (attachmentId: string): string => `ATTACHMENT#${attachmentId}`;
export const TEMPLATE_SK = (templateId: string): string => `TEMPLATE#${templateId}`;

// ─── GSI3 Key Pattern Constants ─────────────────────────────────────────────

/** User's libraries: GSI3PK=USER#{userId}, GSI3SK=LIBRARY#{libraryId} */
export const GSI3_USER_LIBRARIES_PK = (userId: string): string => `USER#${userId}`;
export const GSI3_USER_LIBRARIES_SK = (libraryId: string): string => `LIBRARY#${libraryId}`;

/** Library page tree: GSI3PK=LIBTREE#{libraryId}, GSI3SK=PAGE#{sortOrder}#{pageId} */
export const GSI3_LIBTREE_PK = (libraryId: string): string => `LIBTREE#${libraryId}`;
export const GSI3_LIBTREE_SK = (sortOrder: string, pageId: string): string =>
  `PAGE#${sortOrder}#${pageId}`;

/** Backlinks: GSI3PK=BACKLINK#{targetPageId}, GSI3SK=PAGE#{sourcePageId} */
export const GSI3_BACKLINK_PK = (targetPageId: string): string => `BACKLINK#${targetPageId}`;
export const GSI3_BACKLINK_SK = (sourcePageId: string): string => `PAGE#${sourcePageId}`;

/** Cross-link reverse (card): GSI3PK=XLINK#CARD#{cardId}, GSI3SK=PAGE#{pageId} */
export const GSI3_XLINK_CARD_PK = (cardId: string): string => `XLINK#CARD#${cardId}`;
export const GSI3_XLINK_CARD_SK = (pageId: string): string => `PAGE#${pageId}`;

/** Cross-link reverse (board): GSI3PK=XLINK#BOARD#{boardId}, GSI3SK=PAGE#{pageId} */
export const GSI3_XLINK_BOARD_PK = (boardId: string): string => `XLINK#BOARD#${boardId}`;
export const GSI3_XLINK_BOARD_SK = (pageId: string): string => `PAGE#${pageId}`;

/** Page title search: GSI3PK=PAGETITLE#{libraryId}, GSI3SK={lowercaseTitle}#{pageId} */
export const GSI3_PAGETITLE_PK = (libraryId: string): string => `PAGETITLE#${libraryId}`;
export const GSI3_PAGETITLE_SK = (lowercaseTitle: string, pageId: string): string =>
  `${lowercaseTitle}#${pageId}`;

// ─── Edit Lock Constants & Types ────────────────────────────────────────────

/** Sort key for the edit lock record on a page */
export const EDIT_LOCK_SK = 'EDIT_LOCK';

/** Current state of an editing lock on a page */
export interface EditLockState {
  readonly locked: boolean;
  readonly userId?: string;
  readonly displayName?: string;
  readonly expiresAt?: number;
}

// ─── Edit Lock Event Payloads ───────────────────────────────────────────────

/** Payload for `page.lock.acquired` events */
export interface PageLockAcquiredPayload {
  readonly pageId: string;
  readonly userId: string;
  readonly displayName: string;
}

/** Payload for `page.lock.released` events */
export interface PageLockReleasedPayload {
  readonly pageId: string;
  readonly userId: string;
  readonly displayName: string;
}

/** Payload for `page.lock.taken` events */
export interface PageLockTakenPayload {
  readonly pageId: string;
  readonly previousUserId: string;
  readonly newUserId: string;
  readonly newDisplayName: string;
}
